import { createAsyncThunk } from '@reduxjs/toolkit';
import { AclResource } from 'common/types/Entities';

import { APIAclResourceGetList } from '../Acls/AclResources.api';
import { handleListFetch } from '../apiHelpers';
import { APISuiteGet } from '../ApplicationSuites/ApplicationSuites.api';
import { APITenantGetResponse } from '../Tenants/TenantApiTypeDefs';
import { APITenantGet } from '../Tenants/Tenants.api';

import { APIUserGroupDelete, APIUserGroupGet, APIUserGroupGetResponse, APIUserGroupPost, APIUserGroupPut, APIUserGroupsGetList, APIUserGroupWithCountsGetList, APIUserGroupWithCountsGetResponse } from './UserGroups.api';

export const fetchUserGroups = createAsyncThunk('userGroups/fetchUserGroups', async (params: { tenantId: string, signal?: AbortSignal }) => {
    const response = await APIUserGroupsGetList(params);
    return response.data;
});

type FetchUserGroupDetailsParams = {
    tenantId: string;
    applicationSuiteId: string;
    usergroupId: string;
}

export type FetchUserGroupDetailsResponse = {
    applicationSuiteId: string,
    applicationSuiteName: string,
    tenantId: string,
    tenantName: string,
    tenantRequiresLicense: boolean,
    id: string,
    name: string,
    description: string
}

const _formatUserGroupWithCountsRows = (
    rows: {
        groupId: string;
        name: string;
        userCount: number;
        packageCount: number;
        resourceAccesses: {
            resourceId: string,
            accesses: string[]
        }[];
        cibs: string[];
    }[],
    aclResources: AclResource[]
)  => {
    return rows.map(item => {
        const resourceAccesses = aclResources.reduce((acc: Record<string, string>, resource) => {
            const accesses = item.resourceAccesses.find(r => resource.id === r.resourceId)?.accesses;
            // Use displayName if available, otherwise fall back to name
            const resourceKey = resource.displayName || resource.name;
            acc[resourceKey] = accesses ? accesses.join(',') : '';
            return acc;
        }, {});

        return {
            UserGroupName: item.name,
            CIBS: item.cibs == undefined ? '' : item.cibs.join(','),
            UserCount: item.userCount,
            PackageCount: item.packageCount,
            ...resourceAccesses
        };
    });
};

export const fetchUserGroupDetails = createAsyncThunk('userGroupDetails/fetchDetails', async (fetchParams: FetchUserGroupDetailsParams): Promise<FetchUserGroupDetailsResponse> => {
    const requests = [
        APISuiteGet({ id: fetchParams.applicationSuiteId }),
        APITenantGet({ id: fetchParams.tenantId }),
        APIUserGroupGet({ tenantId: fetchParams.tenantId, id: fetchParams.usergroupId })
    ];
    const responses = await Promise.all(requests);

    const suiteResponse = responses[0];
    const tenantResponse = responses[1] as APITenantGetResponse;
    const userGroupResponse = responses[2] as APIUserGroupGetResponse;

    return {
        applicationSuiteId: suiteResponse.data.id,
        applicationSuiteName: suiteResponse.data.name,
        tenantId: tenantResponse.data.id,
        tenantName: tenantResponse.data.name,
        tenantRequiresLicense: tenantResponse.data.isLicenseRequired,
        id: userGroupResponse.data.id,
        name: userGroupResponse.data.name,
        description: userGroupResponse.data.description,
    };
});

export const postUserGroup = createAsyncThunk('userGroups/postUserGroups', async (item: { tenantId: string, name: string, description: string }) => {
    const response = await APIUserGroupPost(item);
    const rows = await handleListFetch(APIUserGroupsGetList({ tenantId: item.tenantId }));
    return {
        response,
        rows
    };
});

export const deleteUserGroup = createAsyncThunk('userGroups/deleteUserGroups', async (item: { tenantId: string, id: string })=> {
    const response = await APIUserGroupDelete(item);
    const rows = await handleListFetch(APIUserGroupsGetList({ tenantId: item.tenantId }));
    return {
        response,
        rows
    };
});


export const putUserGroup = createAsyncThunk('userGroups/putUserGroup', async (item: { tenantId: string, name: string, description: string, id: string }) => {
    const response = await APIUserGroupPut(item);
    const rows = await handleListFetch(APIUserGroupsGetList({ tenantId: item.tenantId }));
    return {
        response,
        rows
    };
});

export const fetchUserGroupAclsWithCounts = createAsyncThunk('userGroups/counts', async (tenantId: string) => {
    const requests = [
        APIUserGroupWithCountsGetList(tenantId),
        APIAclResourceGetList({ tenantId })
    ];
    const responses = await Promise.all(requests);

    const userGroups = responses[0] as APIUserGroupWithCountsGetResponse;
    const aclResources = responses[1].data as Array<AclResource>;
    return _formatUserGroupWithCountsRows(userGroups.data, aclResources);
});
